// Pure helpers behind "หมวดสินค้าตาม Shopee อัตโนมัติ" — the Shopee global
// category -> seller category map (mdf-bo-fn#282, spec
// docs/specs/2026-09-22-shopee-master-product-sync-design.md §6D.4 HTTP +
// §6D.6 FE).
//
// Vue-free / axios-free on purpose: this repo's jest cannot mount a .vue file
// (see tests/unit/skuWmsOptionsSource.spec.js), so everything decidable — how
// an API row is normalised, which rows a Save must actually PUT, and which
// sentence explains how one product's category was resolved — lives here where
// a spec can call it directly. ShopeeCategoryMap.vue only renders.
//
// --------------------------------------------------------------------------
// THE BACKEND CONTRACT (§6D.4). Built in parallel with this screen, so these
// bytes are the agreement, not an observation of a running server.
//
//   GET  /api/platform/v2/shopee/category-map?platform_shop_id=
//     200 {fetched_at, rows:[{shopee_category_id, name_th, name_en, path_th,
//                             product_count, category_id, category_name,
//                             source}]}
//        rows = every Shopee category THIS SHOP uses (counted from the raw
//        item_base_info) UNION every row already in the map, product_count
//        descending. `source` is "auto" | "manual", and absent/empty on a
//        Shopee category that has no map row yet.
//
//   PUT  /api/platform/v2/shopee/category-map
//        {items:[{shopee_category_id, category_id}]}
//     200 {updated:n}
//        category_id = 0 DELETES the row (next sync auto-resolves again,
//        §6D.1 rule 5) · a category_id that does not exist or was deleted
//        answers 400 {code:"bad_category"}.
//
//   POST /api/platform/v2/shopee/category-map/refresh  {platform_shop_id}
//     200 {count, fetched_at} · 502 when Shopee's get_category fails.
//
//   GET  /api/platform/v2/shopee/categories?platform_shop_id=&q=
//     200 {rows:[{category_id, parent_id, name_th, name_en, path_th,
//                 has_children}]}   (<=50)
//
// And on the sync side (§6D.3), both the single to-seller run and each bulk
// item now carry a resolution object:
//
//   category = {category_id, name, source, would_create, shopee_category_id}
//     source ∈ override | map | existing_name | auto_new | unknown_name
//
// --------------------------------------------------------------------------

// AUTO_CATEGORY_VALUE is the sentinel the seller-category <select> uses for
// "ให้สร้างอัตโนมัติ", and the value a PUT sends to REMOVE a map row. It is 0
// rather than null/"" because §6D.4 spells the delete as category_id=0, and
// because el-select needs a concrete value to bind an option to.
export const AUTO_CATEGORY_VALUE = 0;

// The five resolution sources §6D.3 can answer with. Anything else — a source
// the BE grows later, or a missing/garbled object — falls back to `unknown`
// rather than rendering an empty tag.
const RESOLUTION_TAG_TYPES = {
  // The operator picked the category by hand for this one product: nothing was
  // decided by the map, so it is stated rather than celebrated.
  override: "info",
  // The map answered. This is the steady state the feature exists to reach.
  map: "success",
  // No map row, but a seller category of exactly that name already existed, so
  // it was reused instead of duplicated (§6D.1 rule 8).
  existing_name: "success",
  // A NEW seller category will be / was created. Worth a second look: it is
  // the only source that changes the seller's category list.
  auto_new: "warning",
  // The leaf was not in the Shopee tree even after a refresh, so the category
  // is named "Shopee <id>" (§6D.1 rule 6). A human has to rename it.
  unknown_name: "danger",
  // action=update: the product already lives in the seller catalog and keeps
  // its category (§6D.1 rule 3); the BE reports it so the UI can say so.
  unchanged: "info"
};

function toNumber(value) {
  const n = Number(value);
  return isFinite(n) ? n : 0;
}

// categoryMapRows normalises one GET response's `rows` for the table.
//
// Three things the raw rows cannot be trusted to do, and every one of them has
// bitten this codebase before:
//   * ids arrive as JSON numbers here but as strings through some proxies, and
//     an el-select bound to "12" never matches an option valued 12;
//   * `category_id` is null (not 0) for an unmapped Shopee category, and null
//     would make the "ให้สร้างอัตโนมัติ" option unselectable;
//   * `source` is absent on those same rows, and `undefined` renders as an
//     empty badge instead of "ยังไม่มี map".
// The order is the BE's (product_count desc) but is re-applied here so the
// table is right even if a proxy or a later endpoint returns them unsorted.
// Ties break on shopee_category_id ASCENDING so the list is stable across
// reloads — two categories with 0 products must not swap places every fetch.
export function categoryMapRows(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return list
    .map(function(row) {
      const r = row || {};
      return {
        shopee_category_id: toNumber(r.shopee_category_id),
        name_th: r.name_th === null || r.name_th === undefined ? "" : String(r.name_th),
        name_en: r.name_en === null || r.name_en === undefined ? "" : String(r.name_en),
        path_th: r.path_th === null || r.path_th === undefined ? "" : String(r.path_th),
        product_count: toNumber(r.product_count),
        category_id: toNumber(r.category_id),
        category_name:
          r.category_name === null || r.category_name === undefined ? "" : String(r.category_name),
        source: r.source === null || r.source === undefined ? "" : String(r.source)
      };
    })
    .sort(function(a, b) {
      if (b.product_count !== a.product_count) return b.product_count - a.product_count;
      return a.shopee_category_id - b.shopee_category_id;
    });
}

// categoryMapDiff builds the `items` of the PUT: ONLY the rows whose
// category_id the operator actually changed.
//
// Sending the whole table instead would be wrong twice over. It would stamp
// `source='manual'` (§6D.1 rule 5) on every row the operator never touched,
// freezing rows that were auto-resolved and should keep re-resolving; and it
// would turn one mistyped row into a 400 bad_category that rejects the whole
// save. `original` is the untouched snapshot from the last GET, `edited` is
// what the table binds to; rows are matched on shopee_category_id, not on
// index, because sorting or a background refresh can reorder them.
//
// A row that is in `edited` but not in `original` counts as changed when it
// points somewhere (category_id > 0): that is a Shopee category the operator
// added by hand from the search box. A new row left on "auto" (0) is a no-op
// and is not sent — there is nothing to delete.
export function categoryMapDiff(original, edited) {
  const before = {};
  const originals = Array.isArray(original) ? original : [];
  for (let i = 0; i < originals.length; i++) {
    const row = originals[i] || {};
    before[String(toNumber(row.shopee_category_id))] = toNumber(row.category_id);
  }

  const items = [];
  const list = Array.isArray(edited) ? edited : [];
  for (let i = 0; i < list.length; i++) {
    const row = list[i] || {};
    const shopeeCategoryID = toNumber(row.shopee_category_id);
    if (shopeeCategoryID <= 0) continue;
    const key = String(shopeeCategoryID);
    const now = toNumber(row.category_id);
    const was = Object.prototype.hasOwnProperty.call(before, key) ? before[key] : AUTO_CATEGORY_VALUE;
    if (now === was) continue;
    items.push({ shopee_category_id: shopeeCategoryID, category_id: now });
  }
  return items;
}

// categoryResolutionTag turns one §6D.3 resolution into the el-tag `type` and
// the i18n key that explain it — in the single modal's preview, and in the
// bulk results table.
//
// It never throws and never returns an empty key: a missing object, a missing
// source or a source this build has not heard of all become
// `shopee_category_map.resolution.unknown`, which is a real sentence in both
// dictionaries. A blank tag next to a category name reads as a rendering bug.
export function categoryResolutionTag(res) {
  const source = res && res.source !== null && res.source !== undefined ? String(res.source) : "";
  const type = Object.prototype.hasOwnProperty.call(RESOLUTION_TAG_TYPES, source)
    ? RESOLUTION_TAG_TYPES[source]
    : "";
  if (type === "") {
    return { type: "info", key: "shopee_category_map.resolution.unknown" };
  }
  return { type: type, key: "shopee_category_map.resolution." + source };
}
